/**
 * Lo que se sabe de los avisos de ESTE aparato sin preguntarle al servidor: si
 * puede recibirlos, su service worker y su suscripcion.
 *
 * Vive aparte de `push.ts` por una razon de dependencias: cerrar sesion
 * (`lib/api.ts`, ruta `auth/logout`) tiene que soltar los avisos del aparato, y
 * `push.ts` habla con el servidor POR `api.ts`. Esto no importa nada de eso, asi
 * que la pasarela lo puede llamar sin cerrar un circulo de modulos —que compila
 * callado y revienta en el navegador (CLAUDE.md §2)—.
 */

import { currentEnv, isIos, isStandalone } from './device.js';

export type PushSupport =
  /** Se puede activar aqui. */
  | 'yes'
  /** iPhone o iPad desde Safari: solo llegan con la app en la pantalla de inicio. */
  | 'install'
  /** Este navegador no tiene notificaciones push. */
  | 'no';

/**
 * Web Push es el mismo mecanismo en todos lados, con una condicion por
 * plataforma: en Android y en la computadora basta el navegador, instalada o
 * no; en iPhone y iPad SOLO con la app en la pantalla de inicio (iOS 16.4 en
 * adelante). En Safari suelto `PushManager` ni existe, y por eso se distingue
 * "no se puede" de "se puede si la instalas": no se mira la version, se mira si
 * la pieza esta.
 */
export function pushSupport(): PushSupport {
  if (typeof window === 'undefined') return 'no';
  const has = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  if (has) return 'yes';
  return isIos(currentEnv()) && !isStandalone() ? 'install' : 'no';
}

/**
 * Cuanto se espera a que la app termine de registrar su service worker. La
 * primera vez que se abre, el registro va a medias cuando Configuracion
 * pregunta; en desarrollo no llega nunca (`main.tsx` solo lo registra en
 * produccion), y no se espera para siempre.
 */
const REGISTRATION_WAIT_MS = 5_000;

/**
 * El registro del service worker, sin esperar uno que no va a llegar.
 *
 * Con espera, se devuelve un registro con su trabajador ACTIVO, porque es lo
 * que exige suscribirse. Recien instalada la app, el registro ya existe pero su
 * trabajador todavia se esta instalando, y encender los avisos en esos segundos
 * fallaba con un error del navegador que no explica nada; por eso no basta con
 * que el registro exista, se espera a `ready`.
 *
 * `wait: false` contesta con lo que ya hay, activo o no: leer la suscripcion no
 * lo necesita. Lo usa cerrar sesion, que no puede regalar la espera: en un
 * aparato sin service worker —el de desarrollo— salir tardaria cinco segundos
 * sin que nada lo explique.
 */
export async function pushRegistration(
  { wait }: { wait: boolean } = { wait: true },
): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  const existing = await navigator.serviceWorker.getRegistration('/');
  if (!wait || existing?.active) return existing ?? null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), REGISTRATION_WAIT_MS);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

/** La suscripcion de este aparato, si la tiene. Nunca pide permiso. */
export async function currentPushSubscription(
  options: { wait: boolean } = { wait: true },
): Promise<PushSubscription | null> {
  if (pushSupport() !== 'yes') return null;
  const registration = await pushRegistration(options);
  return (await registration?.pushManager.getSubscription()) ?? null;
}
